<template>
  <main
    class="animated fadeIn faster"
    :class="rootClasses"
  >
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <div
      v-else
      class="client-space"
      :class="{
        'ml-4 mr-4': !fromClient,
        'client-space--from-client': fromClient,
      }"
    >
      <section class="ol-panel">
        <div class="ol-panel__bg"></div>
        <div class="ol-panel__grid">
          <div class="ol-panel__cell">
            <p class="ol-kpi__eyebrow">
              {{ $t("lang.dashboard.client_space_meetings_eyebrow") }}
            </p>
            <div class="ol-kpi__row">
              <div class="ol-kpi__main">
                <span class="ol-kpi__number">{{ totalMeetings }}</span>
                <span class="ol-kpi__unit">{{
                  $t("lang.dashboard.client_space_meetings_unit")
                }}</span>
              </div>
              <!-- DISEÑO DE LA GRÁFICA HARDCODEADO-->
              <div class="ol-spark">
                <svg
                  viewBox="0 0 148 62"
                  preserveAspectRatio="none"
                  class="ol-spark__svg"
                >
                  <defs>
                    <linearGradient
                      id="client-space-spark-fill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stop-color="rgba(255,255,255,.4)"
                      />
                      <stop
                        offset="100%"
                        stop-color="rgba(255,255,255,0)"
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 6 56 C 17.33 56 17.33 33.27 28.67 33.27 C 40 33.27 40 19.64 51.33 19.64 C 62.67 19.64 62.67 42.36 74 42.36 C 85.33 42.36 85.33 6 96.67 6 C 108 6 108 24.18 119.33 24.18 C 130.67 24.18 130.67 6 142 6 L 142 62 L 6 62 Z"
                    fill="url(#client-space-spark-fill)"
                  />
                  <path
                    d="M 6 56 C 17.33 56 17.33 33.27 28.67 33.27 C 40 33.27 40 19.64 51.33 19.64 C 62.67 19.64 62.67 42.36 74 42.36 C 85.33 42.36 85.33 6 96.67 6 C 108 6 108 24.18 119.33 24.18 C 130.67 24.18 130.67 6 142 6"
                    fill="none"
                    stroke="#fff"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <!-- FIN DEL DISEÑO DE LA GRÁFICA HARDCODEADO-->
            <p class="ol-kpi__sub">
              {{ $t("lang.dashboard.client_space_avg_before") }}
              <strong>{{ avgPerMonth }}</strong>
              {{ $t("lang.dashboard.client_space_avg_after") }}
            </p>
          </div>
          <div class="ol-panel__divider"></div>
          <div class="ol-panel__cell">
            <p class="ol-kpi__eyebrow">
              {{ $t("lang.dashboard.client_space_pipeline_eyebrow") }}
            </p>
            <div class="ol-kpi__main">
              <span class="ol-kpi__currency">€</span>
              <span class="ol-kpi__number">{{ formatMoney(pipelineValue) }}</span>
            </div>
            <div class="ol-formula">
              <div class="ol-chip">
                <span class="ol-chip__k">{{
                  $t("lang.dashboard.client_space_rdv_generated")
                }}</span>
                <span class="ol-chip__v">{{ totalMeetings }}</span>
              </div>
              <span class="ol-formula__op">×</span>
              <div class="ol-chip ol-chip--info">
                <span class="ol-chip__k">
                  {{ $t("lang.dashboard.client_space_closing_avg") }}
                  <!-- <em>{{ $t("lang.dashboard.client_space_industry_hr") }}</em> -->
                  <span class="ol-info">
                    <span class="ol-info__icon">i</span>
                    <span class="ol-info__bubble">{{
                      $t("lang.dashboard.client_space_closing_tooltip")
                    }}</span>
                  </span>
                </span>
                <span class="ol-chip__v">{{ closingRateDisplay }} %</span>
              </div>
              <span class="ol-formula__op">×</span>
              <div class="ol-chip">
                <span class="ol-chip__k">{{
                  $t("lang.dashboard.client_space_avg_basket")
                }}</span>
                <span class="ol-chip__v">{{ formatMoney(avgDealValue) }} €</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="ol-grid mb-3">
        <section class="ol-card ol-top">
          <header class="ol-card__head">
            <h2 class="ol-card__title">
              {{ $t("lang.dashboard.client_space_top10_title") }}
            </h2>
            <span class="ol-card__hint">{{
              $t("lang.dashboard.client_space_top10_hint")
            }}</span>
          </header>
          <p v-if="!topMeetings.length" class="ol-top__empty">
            {{ $t("lang.dashboard.client_space_no_meetings") }}
          </p>
          <ul v-else class="ol-top__list">
            <li
              v-for="(item, index) in topMeetingsDisplay"
              :key="item.target_id"
              class="ol-top__item"
              :style="{ animationDelay: index * 55 + 'ms' }"
            >
              <span class="ol-top__rank">{{ index + 1 }}</span>
              <span
                class="ol-top__logo"
                :style="
                  item.hasLogo ? {} : { background: item.avatarGradient }
                "
              >
                <img
                  v-if="item.hasLogo"
                  :src="item.business_logo"
                  :alt="item.business_name"
                  class="ol-top__logo-img"
                />
                <template v-else>{{ item.initials }}</template>
              </span>
              <span class="ol-top__info">
                <span class="ol-top__name">{{ item.business_name }}</span>
                <span class="ol-top__role">{{ item.prospect_name }}</span>
              </span>
              <span class="ol-top__size">
                <span class="ol-top__bar">
                  <span
                    class="ol-top__fill"
                    :style="{ width: item.sizeScore + '%' }"
                  ></span>
                </span>
                <span class="ol-top__emp">{{ item.contactsLabel }}</span>
              </span>
            </li>
          </ul>
        </section>

        <TargetsEuropeMap
          :targets-map="targetsMap"
          :total-targets-active="totalTargetsActive"
          :total-emails-programmed="totalEmailsProgrammed"
          :calls-today="callsToday"
        />
      </div>
    </div>
  </main>
</template>

<script>
import { mapState } from "vuex";
import { clientsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import TargetsEuropeMap from "@/components/Targets/TargetsEuropeMap.vue";

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#5054EB,#3D42B8)",
  "linear-gradient(135deg,#0F6E56,#0B5544)",
  "linear-gradient(135deg,#0E6E7A,#0A5560)",
  "linear-gradient(135deg,#5054EB,#4048C8)",
  "linear-gradient(135deg,#5054EB,#6B6FEF)",
  "linear-gradient(135deg,#1C1C28,#3A3A50)",
  "linear-gradient(135deg,#3D42B8,#2E3399)",
  "linear-gradient(135deg,#0E6E7A,#5054EB)",
  "linear-gradient(135deg,#854F0B,#A32D2D)",
  "linear-gradient(135deg,#5054EB,#0E6E7A)",
];

export default {
  name: "BillingClientSpace",
  components: { LoadingSpinner, TargetsEuropeMap },
  data() {
    return {
      loading: true,
      pipeline: null,
      topMeetings: [],
      targetsMap: [],
      userId: null,
      clientId: null,
    };
  },
  computed: {
    ...mapState({
      isMenuCollapse: (state) => state.sidebar.isNavOpen,
      user: (state) => state.authentication.user,
    }),
    rootClasses() {
      if (this.fromClient) {
        return "client-space-embed";
      }
      return {
        main: true,
        "expand-main": this.isMenuCollapse,
      };
    },
    totalMeetings() {
      return Number(this.pipeline && this.pipeline.history_total_meetings) || 0;
    },
    seniorityMonths() {
      return Number(this.pipeline && this.pipeline.client_seniority_months) || 0;
    },
    avgPerMonth() {
      if (!this.seniorityMonths) return 0;
      const raw = this.totalMeetings / this.seniorityMonths;
      const fraction = raw % 1;
      return fraction <= 0.5 ? Math.floor(raw) : Math.ceil(raw);
    },
    closingRate() {
      return Number(this.pipeline && this.pipeline.signed_client_meeting_rate) || 0;
    },
    closingRateDisplay() {
      return Number.isInteger(this.closingRate)
        ? this.closingRate
        : parseFloat(this.closingRate.toFixed(2));
    },
    avgDealValue() {
      return Number(this.pipeline && this.pipeline.avg_deal_value) || 0;
    },
    pipelineValue() {
      return Math.round(
        this.totalMeetings * (this.closingRate / 100) * this.avgDealValue,
      );
    },
    totalTargetsActive() {
      return (
        Number(this.pipeline && this.pipeline.total_targets_active) || 0
      );
    },
    totalEmailsProgrammed() {
      return (
        Number(this.pipeline && this.pipeline.total_emails_programmed) || 0
      );
    },
    callsToday() {
      const bookers =
        Number(this.pipeline && this.pipeline.dedicated_bookers) || 0;
      return bookers * 200;
    },
    topMeetingsDisplay() {
      if (!this.topMeetings.length) return [];

      const maxContacts = Math.max(
        ...this.topMeetings.map((m) => Number(m.total_contacts) || 0),
      );

      return this.topMeetings.map((meeting, index) => {
        const contacts = Number(meeting.total_contacts) || 0;
        return {
          ...meeting,
          initials: this.getBusinessInitials(meeting.business_name),
          contactsLabel: this.formatContacts(contacts),
          sizeScore:
            maxContacts > 0 ? Math.round((contacts / maxContacts) * 100) : 0,
          avatarGradient: AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length],
          hasLogo: this.hasBusinessLogo(meeting.business_logo),
        };
      });
    },
  },
  props: {
    fromClient: {
      type: Boolean,
      default: false,
    },
    client_id: {
      type: Number,
      required: true,
    },
    user_id: {
      type: Number,
      required: true,
    },
  },
  created() {
    if(this.fromClient) {
      this.userId = this.user_id;
      this.clientId = this.client_id;
    } else {
      this.userId = this.user.id;
      this.clientId = this.user.client_id;
    }
    this.getClientSpace(this.userId, this.clientId);
  },
  methods: {
    getClientSpace(user_id, client_id) {
      this.loading = true;
      clientsService
        .getClientSpace(user_id, client_id)
        .then((response) => {
          this.pipeline = response.pipeline || null;
          this.topMeetings = response.top_meetings || [];
          this.targetsMap = response.targets_map || [];
        })
        .catch((error) => {
          console.error("Error fetching client space:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatMoney(value) {
      return new Intl.NumberFormat("fr-FR").format(Math.round(value));
    },
    formatContacts(value) {
      const num = Number(value) || 0;
      if (num < 1000) return String(num);
      const k = num / 1000;
      if (Number.isInteger(k)) return k + "k";
      return parseFloat(k.toFixed(1)) + "k";
    },
    getBusinessInitials(name) {
      if (!name || typeof name !== "string") return "";
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    hasBusinessLogo(logo) {
      return logo != null && String(logo).trim() !== "";
    },
  },
};
</script>

<style scoped>
.client-space-embed {
  display: block;
  width: 100%;
  min-height: 0;
  margin: 0;
  padding: 0;
  background: transparent;
}

.client-space {
  --ol-radius: 14px;
  --ol-primary: #5054eb;
  --ol-surface: #ffffff;
  --ol-surface-2: #f4f4f6;
  --ol-text-2: #5a5a6e;
  --ol-text-3: #9a9aab;
  --ol-border: #e8e8ee;
}

/* Compact layout only when embedded in client/id (AgencyForm) */
.client-space--from-client {
  margin: 0;
  padding: 12px 8px 8px;
  max-width: 100%;
  box-sizing: border-box;
}

.client-space--from-client .ol-panel {
  margin-bottom: 16px;
  box-shadow: 0 10px 28px rgba(80, 84, 235, 0.16);
}

.client-space--from-client .ol-panel__cell {
  padding: clamp(18px, 2.2vw, 28px);
}

.client-space--from-client .ol-kpi__number {
  font-size: clamp(36px, 4.8vw, 64px);
}

.client-space--from-client .ol-kpi__currency {
  font-size: clamp(20px, 2.4vw, 30px);
}

.client-space--from-client .ol-spark {
  width: 120px;
}

.client-space--from-client .ol-spark__svg {
  height: 52px;
}

.client-space--from-client .ol-grid {
  gap: 16px;
  margin-bottom: 0 !important;
}

.client-space--from-client .ol-card {
  padding: clamp(14px, 1.8vw, 20px);
}

.client-space--from-client .ol-card__title {
  font-size: 15px;
}

@media (max-width: 1200px) {
  .client-space--from-client .ol-grid {
    grid-template-columns: 1fr;
  }
}

.ol-panel {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #5054eb 0%, #5054eb 55%, #3d42b8 100%);
  border-radius: var(--ol-radius);
  margin-bottom: 20px;
  box-shadow: 0 18px 44px rgba(80, 84, 235, 0.22);
}

.ol-panel__bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      600px 320px at 90% -10%,
      rgba(255, 255, 255, 0.18),
      transparent 60%
    ),
    radial-gradient(
      420px 320px at 2% 120%,
      rgba(255, 255, 255, 0.08),
      transparent 60%
    );
}

.ol-panel__grid {
  position: relative;
  display: grid;
  grid-template-columns: 1.1fr 1px 1fr;
  align-items: stretch;
}

@media (max-width: 860px) {
  .ol-panel__grid {
    grid-template-columns: 1fr;
  }

  .ol-panel__divider {
    display: none;
  }
}

.ol-panel__divider {
  background: linear-gradient(
    180deg,
    transparent,
    rgba(255, 255, 255, 0.22),
    transparent
  );
}

.ol-panel__cell {
  padding: clamp(24px, 3vw, 38px);
}

.ol-kpi__eyebrow {
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.72);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 11.5px;
  font-weight: 600;
}

.ol-kpi__row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
}

.ol-kpi__main {
  display: flex;
  align-items: baseline;
  gap: 11px;
}

.ol-kpi__number {
  font-weight: 800;
  color: #fff;
  font-size: clamp(46px, 6.6vw, 80px);
  line-height: 0.85;
  letter-spacing: -0.04em;
}

.ol-kpi__currency {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(24px, 3vw, 38px);
  align-self: flex-start;
  margin-top: 6px;
}

.ol-kpi__unit {
  color: rgba(255, 255, 255, 0.68);
  font-size: 15px;
  font-weight: 600;
}

.ol-kpi__sub {
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 13.5px;
}

.ol-kpi__sub strong {
  color: #fff;
}

.ol-spark {
  width: 148px;
  flex-shrink: 0;
}

.ol-spark__svg {
  width: 100%;
  height: 62px;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.18));
}

.ol-formula {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.ol-chip {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 11px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ol-chip__k {
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.72);
}

.ol-chip__v {
  font-weight: 700;
  font-size: 15px;
  color: #fff;
}

.ol-chip__k em {
  font-style: normal;
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.4);
}

.ol-formula__op {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
  font-size: 15px;
}

.ol-chip--info .ol-chip__k {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ol-info {
  position: relative;
  display: inline-flex;
}

.ol-info__icon {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-size: 9.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  flex-shrink: 0;
}

.ol-info__bubble {
  position: absolute;
  bottom: calc(100% + 9px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  width: 248px;
  background: #1c1c28;
  color: #fff;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.5;
  padding: 11px 13px;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(28, 28, 40, 0.35);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 30;
  text-align: left;
  letter-spacing: 0;
}

.ol-info__bubble::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #1c1c28;
}

.ol-info:hover .ol-info__bubble {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.ol-grid {
  display: grid;
  grid-template-columns: 1fr 1.18fr;
  gap: 20px;
}

@media (max-width: 980px) {
  .ol-grid {
    grid-template-columns: 1fr;
  }
}

.ol-card {
  background: var(--ol-surface);
  border: 1px solid var(--ol-border);
  border-radius: var(--ol-radius);
  padding: clamp(18px, 2.4vw, 26px);
  box-shadow: 0 1px 2px rgba(28, 28, 40, 0.04),
    0 12px 30px rgba(28, 28, 40, 0.05);
}

.ol-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.ol-card__title {
  font-weight: 700;
  font-size: 17px;
  margin: 0;
  letter-spacing: -0.02em;
}

.ol-card__hint {
  font-size: 12px;
  color: var(--ol-text-3);
}

.ol-top__empty {
  margin: 0;
  padding: 24px 8px;
  text-align: center;
  font-size: 13.5px;
  color: var(--ol-text-2);
}

.ol-top__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ol-top__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 8px;
  border-radius: 10px;
  transition: background 0.2s;
  opacity: 0;
  animation: ol-top-slide-in 0.5s forwards;
}

.ol-top__item:hover {
  background: var(--ol-surface-2);
}

@keyframes ol-top-slide-in {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.ol-top__rank {
  font-weight: 700;
  font-size: 13px;
  color: var(--ol-text-3);
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.ol-top__logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(28, 28, 40, 0.16);
  overflow: hidden;
}

.ol-top__logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.ol-top__info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.ol-top__name {
  font-weight: 700;
  font-size: 14.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ol-top__role {
  font-size: 11.5px;
  color: var(--ol-text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ol-top__size {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-shrink: 0;
}

.ol-top__bar {
  width: 58px;
  height: 6px;
  background: var(--ol-surface-2);
  border-radius: 100px;
  overflow: hidden;
}

.ol-top__fill {
  display: block;
  height: 100%;
  border-radius: 100px;
  background: linear-gradient(90deg, var(--ol-primary), #6b6fef);
}

.ol-top__emp {
  font-size: 11.5px;
  color: var(--ol-text-2);
  font-weight: 600;
  width: 38px;
  text-align: right;
}
</style>
